import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { BiLinkExternal } from 'react-icons/bi';
import { useAuth } from '@/auth/AuthContext';
import { STUDENT_AFFAIRS_WRITE_ROLES, hasRole } from '@/auth/roles';
import { getStudentAffairs, updateStudentAffairsFile } from '@/api/studentAffairs.api';
import { ApiError, type ApiErrorDetail } from '@/api/errors';
import { Banner } from '@/components/common/Banner';
import { StatusChip } from '@/components/common/StatusChip';
import { ProgressBar } from '@/components/common/ProgressBar';
import { DocumentRow } from '@/pages/student-affairs/DocumentRow';
import { showErrorToast, showSuccessToast } from '@/lib/toast';
import { formatDate, formatDateTime } from '@/lib/format';
import { lookupLabel } from '@/lib/lookups';
import { toneForStudentStatus } from '@/lib/studentStatus';
import { DOCUMENT_CHECKLIST, STUDENT_STATUSES, TRANSFER_STATUSES } from '@/types/shared';
import type {
  DocumentChecklistRow,
  StudentAffairsResponse,
  StudentStatus,
  TransferStatus,
  UpdateStudentAffairsRequest,
} from '@/types/shared';
import './StudentAffairsDetailPage.css';

interface FileFormState {
  studentStatus: StudentStatus | '';
  /** T184 (§21) — nullable, unlike `studentStatus` above: `''` is a real, selectable "none"
   * option (not just a disabled placeholder), so it round-trips to an explicit `null` on submit
   * when changed away from a set value. See `handleFileSubmit` below. */
  transferStatus: TransferStatus | '';
  receiptNumber: string;
  receiptDate: string;
  fileNotes: string;
  documentsLink: string;
}

type FileFieldErrors = Partial<Record<keyof FileFormState, string>>;

function toFileForm(affairs: StudentAffairsResponse): FileFormState {
  return {
    studentStatus: affairs.studentStatus ?? '',
    transferStatus: affairs.transferStatus ?? '',
    receiptNumber: affairs.receiptNumber ?? '',
    receiptDate: affairs.receiptDate ? affairs.receiptDate.slice(0, 10) : '',
    fileNotes: affairs.fileNotes ?? '',
    documentsLink: affairs.documentsLink ?? '',
  };
}

/** Only http(s) links are safe to store and render as an href — blocks `javascript:`, `data:`,
 * and other schemes. Returns the trimmed URL if valid, else null. */
function normalizedHttpUrl(raw: string): string | null {
  const value = raw.trim();
  if (!value) return null;
  try {
    const { protocol } = new URL(value);
    return protocol === 'http:' || protocol === 'https:' ? value : null;
  } catch {
    return null;
  }
}

export function StudentAffairsDetailPage() {
  const { t, i18n } = useTranslation();
  const { studentCode } = useParams<{ studentCode: string }>();
  const { user } = useAuth();

  const [affairs, setAffairs] = useState<StudentAffairsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [fileForm, setFileForm] = useState<FileFormState | null>(null);
  const [fileFieldErrors, setFileFieldErrors] = useState<FileFieldErrors>({});
  const [fileBanner, setFileBanner] = useState<{ tone: 'success' | 'danger'; text: string } | null>(
    null,
  );
  const [fileSaving, setFileSaving] = useState(false);

  useEffect(() => {
    if (!studentCode) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    getStudentAffairs(studentCode)
      .then((data) => {
        if (cancelled) return;
        setAffairs(data);
        setFileForm(toFileForm(data));
      })
      .catch((err) => {
        if (cancelled) return;
        const apiError = ApiError.fromUnknown(err);
        setLoadError(
          apiError.code === 'NOT_FOUND'
            ? t('studentAffairs.detail.notFound')
            : t('studentAffairs.detail.loadError'),
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [studentCode, t]);

  const orderedDocuments = useMemo<DocumentChecklistRow[]>(() => {
    if (!affairs) return [];
    const byType = new Map(affairs.documents.map((d) => [d.documentType, d]));
    return DOCUMENT_CHECKLIST.map((type) => byType.get(type)).filter(
      (d): d is DocumentChecklistRow => Boolean(d),
    );
  }, [affairs]);

  function handleDocumentSaved(updated: DocumentChecklistRow) {
    setAffairs((prev) => {
      if (!prev) return prev;
      const documents = prev.documents.map((d) =>
        d.documentType === updated.documentType ? updated : d,
      );
      const documentsReceived = documents.filter((d) => d.receivedStatus !== 'Not Received').length;
      return { ...prev, documents, documentsReceived };
    });
  }

  const fileDirty = useMemo(() => {
    if (!affairs || !fileForm) return false;
    const initial = toFileForm(affairs);
    return (
      fileForm.studentStatus !== initial.studentStatus ||
      fileForm.transferStatus !== initial.transferStatus ||
      fileForm.receiptNumber !== initial.receiptNumber ||
      fileForm.receiptDate !== initial.receiptDate ||
      fileForm.fileNotes !== initial.fileNotes ||
      fileForm.documentsLink !== initial.documentsLink
    );
  }, [affairs, fileForm]);

  function applyServerFieldErrors(details: ApiErrorDetail[]) {
    const errors: FileFieldErrors = {};
    for (const detail of details) {
      if (
        detail.field &&
        [
          'studentStatus',
          'transferStatus',
          'receiptNumber',
          'receiptDate',
          'fileNotes',
          'documentsLink',
        ].includes(detail.field)
      ) {
        errors[detail.field as keyof FileFormState] = detail.message;
      }
    }
    setFileFieldErrors(errors);
  }

  async function handleFileSubmit(e: FormEvent) {
    e.preventDefault();
    if (!affairs || !fileForm || !studentCode) return;

    const trimmedLink = fileForm.documentsLink.trim();
    if (trimmedLink && !normalizedHttpUrl(trimmedLink)) {
      setFileFieldErrors({ documentsLink: t('studentAffairs.detail.file.documentsLink.invalid') });
      return;
    }

    const initial = toFileForm(affairs);
    const payload: UpdateStudentAffairsRequest = {};
    if (fileForm.studentStatus && fileForm.studentStatus !== initial.studentStatus) {
      payload.studentStatus = fileForm.studentStatus;
    }
    // Unlike studentStatus above, transferStatus is nullable via a real "none" option, so a
    // change is sent through as-is: a set value round-trips to that value, "none" round-trips
    // to an explicit `null` (clears it). Omitted entirely (not in `payload`) when unchanged.
    if (fileForm.transferStatus !== initial.transferStatus) {
      payload.transferStatus = fileForm.transferStatus === '' ? null : fileForm.transferStatus;
    }
    if (fileForm.receiptNumber !== initial.receiptNumber) {
      payload.receiptNumber = fileForm.receiptNumber.trim() || null;
    }
    if (fileForm.receiptDate !== initial.receiptDate) {
      payload.receiptDate = fileForm.receiptDate || null;
    }
    if (fileForm.fileNotes !== initial.fileNotes) {
      payload.fileNotes = fileForm.fileNotes.trim() || null;
    }
    if (fileForm.documentsLink !== initial.documentsLink) {
      payload.documentsLink = fileForm.documentsLink.trim() || null;
    }
    if (Object.keys(payload).length === 0) return;

    setFileSaving(true);
    setFileBanner(null);
    setFileFieldErrors({});
    try {
      const updated = await updateStudentAffairsFile(studentCode, payload);
      setAffairs(updated);
      setFileForm(toFileForm(updated));
      const message = t('studentAffairs.detail.file.saveSuccess');
      setFileBanner({ tone: 'success', text: message });
      showSuccessToast(message);
    } catch (err) {
      const apiError = ApiError.fromUnknown(err);
      if (apiError.code === 'NETWORK_ERROR') {
        const message = t('studentAffairs.detail.file.networkError');
        setFileBanner({ tone: 'danger', text: message });
        showErrorToast(message);
      } else {
        setFileBanner({
          tone: 'danger',
          text: apiError.message || t('studentAffairs.detail.file.saveError'),
        });
        if (apiError.details.length > 0) applyServerFieldErrors(apiError.details);
      }
    } finally {
      setFileSaving(false);
    }
  }

  if (loading) {
    return <p className="fa-form-loading">{t('common.loading')}</p>;
  }

  if (loadError || !affairs || !fileForm) {
    return (
      <div>
        <Banner tone="danger" assertive>
          {loadError ?? t('studentAffairs.detail.notFound')}
        </Banner>
        <Link to="/student-affairs" className="fa-back-link">
          <span className="fa-back-link__arrow" aria-hidden="true" />
          {t('studentAffairs.detail.backToList')}
        </Link>
      </div>
    );
  }

  const { student } = affairs;
  /**
   * Server-computed per record, NOT derived from the role (2026-08 sales write access): a
   * `Sales Agent` may edit only students whose converted lead is assigned to them, which the client
   * has no way to know. Every other write role gets a constant `true` from the API, so this stays
   * equivalent to the old role check for them.
   */
  const canEdit = affairs.canEdit;
  // Distinguishes "your role can never edit this screen" from "your role can, but not THIS
  // student" — otherwise an agent sees a bare read-only badge and reads it as a bug.
  const blockedByOwnership = !canEdit && hasRole(user?.role, STUDENT_AFFAIRS_WRITE_ROLES);

  return (
    <section className="fa-student-affairs-detail">
      <Link to="/student-affairs" className="fa-back-link">
        <span className="fa-back-link__arrow" aria-hidden="true" />
        {t('studentAffairs.detail.backToList')}
      </Link>

      <header className="fa-student-affairs-detail__header">
        <div className="fa-student-affairs-detail__heading">
          <div>
            <h1 className="fa-student-affairs-detail__title">
              <span className="fa-ltr-isolate">{student.studentCode}</span>
              {' — '}
              {student.nameAr}
            </h1>
            <p className="fa-student-affairs-detail__subtitle">{student.nameEn}</p>
            <div className="fa-student-affairs-detail__badges">
              {affairs.studentStatus ? (
                <StatusChip
                  label={t(`enums.studentStatus.${affairs.studentStatus}`)}
                  tone={toneForStudentStatus(affairs.studentStatus)}
                />
              ) : (
                <span className="fa-student-affairs-detail__muted">
                  {t('studentAffairs.notSet')}
                </span>
              )}
              {!canEdit && (
                <span
                  className="fa-chip fa-chip--neutral"
                  title={blockedByOwnership ? t('studentAffairs.detail.notAssignedHint') : undefined}
                >
                  {blockedByOwnership
                    ? t('studentAffairs.detail.notAssignedBadge')
                    : t('studentAffairs.detail.readOnlyBadge')}
                </span>
              )}
            </div>
          </div>
        </div>
        <Link to={`/students/${student.studentCode}`} className="fa-btn fa-btn--secondary">
          {t('studentAffairs.detail.viewAdmissions')}
        </Link>
      </header>

      <div className="fa-student-affairs-detail__grid">
        <section className="fa-form-section">
          <h2 className="fa-form-section__title">{t('studentAffairs.detail.studentInfoTitle')}</h2>
          <dl className="fa-detail-list">
            <DetailRow
              label={t('studentAffairs.detail.dateOfBirth')}
              value={formatDate(student.dateOfBirth, i18n.language)}
            />
            <DetailRow
              label={t('studentAffairs.detail.gender')}
              value={t(`enums.gender.${student.gender}`)}
            />
            <DetailRow
              label={t('studentAffairs.detail.grade')}
              value={lookupLabel(student.grade, i18n.language)}
            />
            <DetailRow
              label={t('studentAffairs.detail.school')}
              value={lookupLabel(student.agreedSchool, i18n.language)}
            />
            <DetailRow
              label={t('studentAffairs.detail.educationalAdministration')}
              value={lookupLabel(student.educationalAdministration, i18n.language)}
            />
            <DetailRow
              label={t('studentAffairs.detail.guardianName')}
              value={student.guardianNameAr || student.guardianNameEn || '—'}
            />
            <DetailRow
              label={t('studentAffairs.detail.guardianPhone')}
              value={student.guardianPhone}
              ltr
            />
            <DetailRow
              label={t('studentAffairs.detail.guardianEmail')}
              value={student.guardianEmail ?? '—'}
              ltr
            />
          </dl>
        </section>

        <section className="fa-form-section">
          <h2 className="fa-form-section__title">{t('studentAffairs.detail.file.title')}</h2>
          {fileBanner && (
            <Banner tone={fileBanner.tone} assertive={fileBanner.tone === 'danger'}>
              {fileBanner.text}
            </Banner>
          )}
          <form onSubmit={handleFileSubmit} noValidate>
            <div className="fa-field">
              <label htmlFor="studentStatus">{t('studentAffairs.detail.file.studentStatus')}</label>
              <select
                id="studentStatus"
                value={fileForm.studentStatus}
                disabled={!canEdit || fileSaving}
                aria-invalid={Boolean(fileFieldErrors.studentStatus)}
                onChange={(e) =>
                  setFileForm((prev) =>
                    prev ? { ...prev, studentStatus: e.target.value as StudentStatus } : prev,
                  )
                }
              >
                <option value="" disabled>
                  {t('studentAffairs.notSet')}
                </option>
                {STUDENT_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {t(`enums.studentStatus.${status}`)}
                  </option>
                ))}
              </select>
              {fileFieldErrors.studentStatus && (
                <p className="fa-field__error">{fileFieldErrors.studentStatus}</p>
              )}
            </div>

            <div className="fa-field">
              <label htmlFor="transferStatus">
                {t('studentAffairs.detail.file.transferStatus')}
              </label>
              <select
                id="transferStatus"
                value={fileForm.transferStatus}
                disabled={!canEdit || fileSaving}
                aria-invalid={Boolean(fileFieldErrors.transferStatus)}
                onChange={(e) =>
                  setFileForm((prev) =>
                    prev
                      ? { ...prev, transferStatus: e.target.value as TransferStatus | '' }
                      : prev,
                  )
                }
              >
                <option value="">{t('studentAffairs.detail.file.transferStatusNone')}</option>
                {TRANSFER_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {t(`enums.transferStatus.${status}`)}
                  </option>
                ))}
              </select>
              {fileFieldErrors.transferStatus && (
                <p className="fa-field__error">{fileFieldErrors.transferStatus}</p>
              )}
            </div>

            <div className="fa-field-row">
              <div className="fa-field">
                <label htmlFor="receiptNumber">
                  {t('studentAffairs.detail.file.receiptNumber')}
                </label>
                <input
                  id="receiptNumber"
                  type="text"
                  className="fa-ltr-isolate"
                  maxLength={30}
                  value={fileForm.receiptNumber}
                  disabled={!canEdit || fileSaving}
                  aria-invalid={Boolean(fileFieldErrors.receiptNumber)}
                  onChange={(e) =>
                    setFileForm((prev) =>
                      prev ? { ...prev, receiptNumber: e.target.value } : prev,
                    )
                  }
                />
                {fileFieldErrors.receiptNumber && (
                  <p className="fa-field__error">{fileFieldErrors.receiptNumber}</p>
                )}
              </div>
              <div className="fa-field">
                <label htmlFor="receiptDate">{t('studentAffairs.detail.file.receiptDate')}</label>
                <input
                  id="receiptDate"
                  type="date"
                  value={fileForm.receiptDate}
                  disabled={!canEdit || fileSaving}
                  aria-invalid={Boolean(fileFieldErrors.receiptDate)}
                  onChange={(e) =>
                    setFileForm((prev) => (prev ? { ...prev, receiptDate: e.target.value } : prev))
                  }
                />
                {fileFieldErrors.receiptDate && (
                  <p className="fa-field__error">{fileFieldErrors.receiptDate}</p>
                )}
              </div>
            </div>

            <div className="fa-field">
              <span className="fa-field__label">{t('studentAffairs.detail.file.receivedBy')}</span>
              <p className="fa-student-affairs-detail__readonly-value">
                {affairs.receivedBy?.fullName ?? t('studentAffairs.detail.file.receivedByEmpty')}
              </p>
            </div>

            <div className="fa-field">
              <label htmlFor="fileNotes">{t('studentAffairs.detail.file.fileNotes')}</label>
              <textarea
                id="fileNotes"
                value={fileForm.fileNotes}
                disabled={!canEdit || fileSaving}
                onChange={(e) =>
                  setFileForm((prev) => (prev ? { ...prev, fileNotes: e.target.value } : prev))
                }
              />
            </div>

            <div className="fa-field">
              <label htmlFor="documentsLink">
                {t('studentAffairs.detail.file.documentsLink.label')}
              </label>
              <div className="fa-student-affairs-detail__link-row">
                <input
                  id="documentsLink"
                  type="url"
                  className="fa-ltr-isolate"
                  maxLength={2048}
                  placeholder={t('studentAffairs.detail.file.documentsLink.placeholder')}
                  value={fileForm.documentsLink}
                  disabled={!canEdit || fileSaving}
                  aria-invalid={Boolean(fileFieldErrors.documentsLink)}
                  onChange={(e) =>
                    setFileForm((prev) =>
                      prev ? { ...prev, documentsLink: e.target.value } : prev,
                    )
                  }
                />
                {normalizedHttpUrl(fileForm.documentsLink) && (
                  <a
                    href={normalizedHttpUrl(fileForm.documentsLink) as string}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="fa-student-affairs-detail__link-open"
                    aria-label={t('studentAffairs.detail.file.documentsLink.open')}
                    title={t('studentAffairs.detail.file.documentsLink.open')}
                  >
                    <BiLinkExternal aria-hidden="true" />
                  </a>
                )}
              </div>
              {fileFieldErrors.documentsLink && (
                <p className="fa-field__error">{fileFieldErrors.documentsLink}</p>
              )}
            </div>

            {canEdit && (
              <div className="fa-student-affairs-detail__file-actions">
                <button
                  type="submit"
                  className="fa-btn fa-btn--primary"
                  disabled={!fileDirty || fileSaving}
                >
                  {fileSaving ? t('students.form.submitting') : t('common.save')}
                </button>
              </div>
            )}
          </form>
        </section>
      </div>

      <section className="fa-form-section fa-student-affairs-detail__documents">
        <div className="fa-student-affairs-detail__documents-heading">
          <h2 className="fa-form-section__title">{t('studentAffairs.detail.documents.title')}</h2>
          <span className="fa-student-affairs-detail__documents-progress">
            <ProgressBar
              value={affairs.documentsReceived}
              max={affairs.documentsTotal}
              label={t('studentAffairs.documentsValue', {
                received: affairs.documentsReceived,
                total: affairs.documentsTotal,
              })}
            />
            <bdi>
              {t('studentAffairs.documentsValue', {
                received: affairs.documentsReceived,
                total: affairs.documentsTotal,
              })}
            </bdi>
          </span>
        </div>

        {canEdit && (
          <div className="fa-doc-row fa-doc-row--header" aria-hidden="true">
            <span>{t('studentAffairs.documents.columns.document')}</span>
            <span>{t('studentAffairs.documents.columns.status')}</span>
            <span>{t('studentAffairs.documents.columns.copies')}</span>
            <span>{t('studentAffairs.documents.columns.receivedDate')}</span>
            <span>{t('studentAffairs.documents.columns.notes')}</span>
            <span />
          </div>
        )}

        <div
          className={`fa-student-affairs-detail__documents-list${
            canEdit ? ' fa-student-affairs-detail__documents-list--editable' : ''
          }`}
        >
          {orderedDocuments.map((doc) => (
            <DocumentRow
              key={doc.documentType}
              studentCode={student.studentCode}
              row={doc}
              canEdit={canEdit}
              onSaved={handleDocumentSaved}
            />
          ))}
        </div>
      </section>

      <section className="fa-form-section fa-student-affairs-detail__audit">
        <h2 className="fa-form-section__title">{t('studentAffairs.detail.auditTitle')}</h2>
        <div className="fa-student-affairs-detail__audit-grid">
          <p>
            <span className="fa-student-affairs-detail__audit-label">
              {t('studentAffairs.detail.addedBy')}
            </span>
            <span>
              {affairs.createdBy?.fullName ?? t('studentAffairs.unknownUser')}
              {' — '}
              <bdi>{formatDateTime(affairs.createdAt, i18n.language)}</bdi>
            </span>
          </p>
          <p>
            <span className="fa-student-affairs-detail__audit-label">
              {t('studentAffairs.detail.updatedBy')}
            </span>
            <span>
              {affairs.updatedBy?.fullName ?? t('studentAffairs.unknownUser')}
              {' — '}
              <bdi>{formatDateTime(affairs.updatedAt, i18n.language)}</bdi>
            </span>
          </p>
        </div>
      </section>
    </section>
  );
}

function DetailRow({ label, value, ltr }: { label: string; value: string; ltr?: boolean }) {
  return (
    <div className="fa-detail-row">
      <dt>{label}</dt>
      <dd className={ltr ? 'fa-ltr-isolate' : undefined}>{value}</dd>
    </div>
  );
}
